// ── Generation Flow Card ─────────────────────────────────────
// Shows real-time chain-of-thought during tree generation.
// Each pipeline stage is a mini-node with live status updates.
// Phase A+B: novelty engine results delegated to NoveltyInspector (tabbed).

import React, { useState } from 'react';
import NoveltyInspector from '../NoveltyInspector';

const STATUS_ICONS = {
  pending: '○',
  active: '●',
  done: '✓',
  error: '✗',
};

function formatElapsed(startedAt, completedAt) {
  if (!startedAt) return '';
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const ms = end - new Date(startedAt).getTime();
  if (ms < 1000) return '';
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

function scoreColor(s) {
  if (s == null) return '#666';
  if (s >= 70) return '#34d399'; // green
  if (s >= 40) return '#fbbf24'; // yellow
  return '#f87171'; // red
}

function scoreLabel(s) {
  if (s == null) return '—';
  if (s >= 90) return 'Reframes';
  if (s >= 70) return 'Notable';
  if (s >= 50) return 'Variant';
  if (s >= 30) return 'Common';
  return 'Cliché';
}

function NoveltyScoreSection({ noveltyScores, noveltySummary }) {
  const entries = Object.entries(noveltyScores || {});
  if (!entries.length) return null;
  const valid = entries.filter(([, v]) => typeof v.score === 'number');
  const avg = noveltySummary?.avgNovelty
    ?? (valid.length ? Math.round(valid.reduce((s, [, v]) => s + v.score, 0) / valid.length) : null);
  const top = entries
    .filter(([, v]) => typeof v.score === 'number')
    .sort((a, b) => b[1].score - a[1].score)
    .slice(0, 3);
  return (
    <div className="novelty-card-section" style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #2a2a2a' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 12, color: '#999' }}>🧬 NOVELTY SCORES</span>
        <span style={{ fontSize: 11, color: '#666' }}>{valid.length}/{entries.length} scored</span>
        {avg != null && (
          <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 600, color: scoreColor(avg) }}>
            avg {avg}/100 · {scoreLabel(avg)}
          </span>
        )}
      </div>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Top 3 most novel:</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {top.map(([nid, v]) => (
          <div key={nid} style={{ display: 'flex', gap: 8, fontSize: 12, alignItems: 'baseline' }}>
            <span style={{ color: scoreColor(v.score), fontWeight: 700, minWidth: 32 }}>{v.score}</span>
            <span style={{ color: '#bbb', flex: 1 }}>
              vs <em style={{ color: '#888' }}>{v.closestExisting || 'unknown'}</em>
              {v.whyNovel && <span style={{ color: '#aaa' }}> — {v.whyNovel}</span>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DmadSection({ dmadResults }) {
  const [openId, setOpenId] = useState(null);
  if (!dmadResults?.length) return null;
  return (
    <div className="dmad-card-section" style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #2a2a2a' }}>
      <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>
        🧠 HETEROGENEOUS REASONERS ({dmadResults.length}/6)
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {dmadResults.map((r) => {
          const isOpen = openId === r.id;
          const hasErr = !!r.error;
          return (
            <div key={r.id} style={{ background: '#181818', borderRadius: 4, padding: '6px 8px' }}>
              <button
                onClick={() => setOpenId(isOpen ? null : r.id)}
                style={{
                  background: 'none', border: 'none', color: '#ccc', cursor: 'pointer',
                  fontSize: 12, padding: 0, width: '100%', textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}
              >
                <span style={{ color: hasErr ? '#f87171' : '#34d399' }}>{isOpen ? '▾' : '▸'}</span>
                <span style={{ fontWeight: 600 }}>{r.label}</span>
                <span style={{ fontSize: 10, color: '#666' }}>{r.method}</span>
                <span style={{ marginLeft: 'auto', fontSize: 10, color: '#666' }}>
                  {r.model} · {Math.round(r.ms/100)/10}s
                </span>
              </button>
              {isOpen && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#bbb', paddingLeft: 16 }}>
                  {hasErr ? (
                    <div style={{ color: '#f87171' }}>Error: {r.error}</div>
                  ) : (
                    <>
                      <div style={{ marginBottom: 8, fontStyle: 'italic' }}>{r.output?.critique}</div>
                      {(r.output?.transformations || []).map((t, i) => (
                        <div key={i} style={{ marginBottom: 6, paddingLeft: 8, borderLeft: '2px solid #444' }}>
                          <div style={{ color: '#ddd' }}><strong>{t.what}</strong></div>
                          {t.why && <div style={{ color: '#888', fontSize: 11 }}>↳ {t.why}</div>}
                          {t.expectedShift && <div style={{ color: '#34d399', fontSize: 11 }}>→ {t.expectedShift}</div>}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function QdArchiveSection({ qdArchive }) {
  const [expanded, setExpanded] = useState(false);
  if (!qdArchive) return null;
  const { stats, dimensions, cells } = qdArchive;
  const cellList = Object.entries(cells)
    .map(([key, c]) => ({ key, ...c }))
    .sort((a, b) => (b.novelty?.score || 0) - (a.novelty?.score || 0));
  // 2D grid: mechanism (rows) × risk (cols) with the highest-quality cell label per crossing
  const mechBins = dimensions?.mechanism || [];
  const riskBins = dimensions?.risk || [];
  const grid = {};
  cellList.forEach(c => {
    const k = `${c.tag.mechanism}|${c.tag.risk}`;
    if (!grid[k] || c.quality > grid[k].quality) grid[k] = c;
  });
  return (
    <div className="qd-card-section" style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #2a2a2a' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 12, color: '#999' }}>🗺 QUALITY-DIVERSITY ARCHIVE</span>
        <span style={{ fontSize: 11, color: '#666' }}>
          {stats.filledCells}/{stats.totalCells} cells · {Math.round(stats.coverage * 100)}% coverage
        </span>
      </div>
      <div style={{ fontSize: 10, color: '#777', marginBottom: 8 }}>
        Mechanism × Risk grid — best idea per cell shown
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 10, color: '#bbb' }}>
          <thead>
            <tr>
              <th style={{ padding: 4, textAlign: 'left', color: '#666' }}></th>
              {riskBins.map(r => <th key={r} style={{ padding: 4, color: '#888', borderBottom: '1px solid #333', minWidth: 90 }}>{r}</th>)}
            </tr>
          </thead>
          <tbody>
            {mechBins.map(m => (
              <tr key={m}>
                <td style={{ padding: 4, color: '#888', borderRight: '1px solid #333', whiteSpace: 'nowrap' }}>{m}</td>
                {riskBins.map(r => {
                  const cell = grid[`${m}|${r}`];
                  return (
                    <td key={r} style={{ padding: 4, verticalAlign: 'top', border: '1px solid #1f1f1f', background: cell ? '#161616' : '#0a0a0a' }}>
                      {cell ? (
                        <div title={`Novelty: ${cell.novelty?.score ?? '?'} · Quality: ${cell.quality}`}>
                          <span style={{ color: scoreColor(cell.novelty?.score), fontWeight: 600 }}>{cell.novelty?.score ?? '—'}</span>{' '}
                          <span style={{ color: '#ccc' }}>{(cell.label || '').slice(0, 40)}</span>
                        </div>
                      ) : <span style={{ color: '#333' }}>—</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{ background: 'none', border: 'none', color: '#888', fontSize: 11, cursor: 'pointer', padding: '6px 0' }}
      >
        {expanded ? '▾' : '▸'} all {Object.keys(cells).length} survivors (audience+horizon collapsed in grid above)
      </button>
      {expanded && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
          {cellList.map(c => (
            <div key={c.key} style={{ fontSize: 11, display: 'flex', gap: 8 }}>
              <span style={{ color: scoreColor(c.novelty?.score), minWidth: 28, fontWeight: 700 }}>{c.novelty?.score ?? '—'}</span>
              <span style={{ color: '#666', minWidth: 200, fontSize: 10 }}>{c.tag.mechanism}/{c.tag.risk}/{c.tag.audience}/{c.tag.horizon}</span>
              <span style={{ color: '#bbb', flex: 1 }}>{c.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RecombinationSection({ recombinations, blendScores }) {
  const [openId, setOpenId] = useState(null);
  if (!recombinations?.length) return null;
  return (
    <div className="recomb-card-section" style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #2a2a2a' }}>
      <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>
        🧬 RECOMBINATIONS ({recombinations.length})
        <span style={{ fontSize: 10, color: '#666', marginLeft: 8 }}>
          cross-cell blends — most novel ideas are usually here
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {recombinations.map((r, i) => {
          const id = `blend-${i}`;
          const isOpen = openId === id;
          const score = blendScores[id]?.score;
          const hasErr = !!r.error;
          return (
            <div key={id} style={{ background: '#181818', borderRadius: 4, padding: '6px 8px' }}>
              <button
                onClick={() => setOpenId(isOpen ? null : id)}
                style={{ background: 'none', border: 'none', color: '#ccc', cursor: 'pointer', fontSize: 12, padding: 0, width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}
              >
                <span style={{ color: hasErr ? '#f87171' : '#a78bfa' }}>{isOpen ? '▾' : '▸'}</span>
                {!hasErr && score != null && (
                  <span style={{ color: scoreColor(score), fontWeight: 700, minWidth: 24 }}>{score}</span>
                )}
                <span style={{ fontWeight: 600 }}>{r.label || r.parents?.map(p => p.label).join(' × ')}</span>
                <span style={{ marginLeft: 'auto', fontSize: 10, color: '#666' }}>{Math.round((r.ms||0)/100)/10}s</span>
              </button>
              {isOpen && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#bbb', paddingLeft: 16 }}>
                  {hasErr ? (
                    <div style={{ color: '#f87171' }}>Error: {r.error}</div>
                  ) : (
                    <>
                      <div style={{ color: '#888', fontSize: 11, marginBottom: 4 }}>
                        <em>Strategy:</em> {r.blendStrategy}
                      </div>
                      <div style={{ color: '#ddd', marginBottom: 8 }}>{r.reasoning}</div>
                      <div style={{ fontSize: 11, paddingLeft: 8, borderLeft: '2px solid #444' }}>
                        <div style={{ color: '#888' }}><strong>From A:</strong> {r.inheritsFrom?.a}</div>
                        <div style={{ color: '#888' }}><strong>From B:</strong> {r.inheritsFrom?.b}</div>
                      </div>
                      {r.parents && (
                        <div style={{ marginTop: 6, fontSize: 10, color: '#555' }}>
                          parents: {r.parents.map(p => `"${(p.label||'').slice(0,30)}"`).join(' × ')}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function GenerationFlowCard({ stages, isComplete, totalTime, nodeCount, noveltyScores, dmadResults, noveltySummary, qdArchive, recombinations, blendScores }) {
  if (!stages?.length) return null;

  return (
    <div className={`gen-flow-card ${isComplete ? 'gen-flow-card--done' : ''}`}>
      <div className="gen-flow-header">
        <span className="gen-flow-icon">
          {isComplete ? '✓' : <span className="refine-pulse">●</span>}
        </span>
        <span className="gen-flow-title">
          {isComplete ? 'GENERATION COMPLETE' : 'GENERATING'}
        </span>
        {isComplete && totalTime && (
          <span className="gen-flow-total-time">{totalTime}</span>
        )}
        {isComplete && nodeCount > 0 && (
          <span className="gen-flow-node-count">{nodeCount} nodes</span>
        )}
      </div>

      <div className="gen-flow-stages">
        {stages.map((stage, i) => (
          <div key={stage.id} className={`gen-flow-stage gen-flow-stage--${stage.status}`}>
            {/* Connector line */}
            {i > 0 && <div className={`gen-flow-connector ${stage.status === 'done' ? 'gen-flow-connector--done' : ''}`} />}

            <div className="gen-flow-stage-row">
              <span className={`gen-flow-stage-icon gen-flow-stage-icon--${stage.status}`}>
                {stage.status === 'active' ? <span className="refine-pulse">●</span> : STATUS_ICONS[stage.status]}
              </span>
              <span className="gen-flow-stage-label">{stage.label}</span>
              {stage.nodeCount > 0 && (
                <span className="gen-flow-stage-badge">{stage.nodeCount}</span>
              )}
              {stage.detail && (
                <span className="gen-flow-stage-detail">{stage.detail}</span>
              )}
              <span className="gen-flow-stage-time">
                {formatElapsed(stage.startedAt, stage.completedAt)}
              </span>
            </div>
          </div>
        ))}
      </div>
      <NoveltyInspector noveltySummary={noveltySummary} />
    </div>
  );
}
